<template>
  <div class="leaderboard-wrap">
    <div class="leaderboard-card">
      <!-- Header -->
      <div class="card-header">
        <div>
          <p class="card-subtitle">Statistik Aktivitas</p>
          <h2 class="card-title">User Activity</h2>
        </div>
        <div class="summary-pills">
          <span class="pill pill-blue">
            <span class="pill-dot blue"></span>
            {{ activeUsers }} Aktif
          </span>
          <span class="pill pill-gray">
            <span class="pill-dot gray"></span>
            {{ inactiveUsers }} Tidak Aktif
          </span>
        </div>
      </div>

      <!-- Divider -->
      <div class="divider"></div>

      <!-- Column Labels -->
      <div class="col-labels">
        <span>Ranking</span>
        <span></span>
        <span>Kunjungan</span>
        <span>Mengubah data</span>
      </div>

      <!-- Leaderboard Rows -->
      <div class="leaderboard-list">
        <div
          v-for="(user, index) in rankedUsers"
          :key="user.id"
          class="leaderboard-row"
          :class="{ 'row-inactive': isInactive(user), 'row-top': index === 0 }"
        >
          <!-- Rank -->
          <div class="rank">
            <span v-if="index === 0" class="rank-medal gold">1</span>
            <span v-else-if="index === 1" class="rank-medal silver">2</span>
            <span v-else-if="index === 2" class="rank-medal bronze">3</span>
            <span v-else class="rank-number">{{ index + 1 }}</span>
          </div>

          <!-- Avatar + Name -->
          <div class="user-cell">
            <div class="avatar" :style="{ background: avatarBg(user.name) }">
              {{ user.name.charAt(0).toUpperCase() }}
            </div>
            <span class="user-name" :class="{ 'name-muted': isInactive(user) }">
              {{ user.name }}
            </span>
          </div>

          <!-- Visits -->
          <div class="stat-cell">
            <span class="stat-value" :class="{ muted: isInactive(user) }">
              {{ user.visited_count.toLocaleString() }}
            </span>
            <div class="mini-bar-wrap">
              <div
                class="mini-bar visits-bar"
                :style="{ width: barWidth(user.visited_count) + '%' }"
              ></div>
            </div>
          </div>

          <!-- Edits -->
          <div class="stat-cell">
            <span class="stat-value" :class="{ muted: isInactive(user) }">
              {{ user.mengubah_data.toLocaleString() }}
            </span>
            <div class="mini-bar-wrap">
              <div
                class="mini-bar edits-bar"
                :style="{ width: barWidth(user.mengubah_data, 'edits') + '%' }"
              ></div>
            </div>
          </div>

          <!-- Status Badge -->
          <div class="status-cell">
            <span
              class="badge"
              :class="isInactive(user) ? 'badge-idle' : 'badge-active'"
            >
              {{ isInactive(user) ? "Idle" : "Aktif" }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "UserActivityLeaderboard",
  data() {
    return {
      userData: [
        { id: 1, name: "superadmin", visited_count: 552, mengubah_data: 27 },
        { id: 8, name: "Lisna", visited_count: 0, mengubah_data: 0 },
        { id: 9, name: "Galih", visited_count: 0, mengubah_data: 0 },
        { id: 10, name: "Ina", visited_count: 0, mengubah_data: 0 },
        { id: 11, name: "Tri", visited_count: 0, mengubah_data: 0 },
        { id: 12, name: "Damar", visited_count: 0, mengubah_data: 0 },
        { id: 15, name: "guest", visited_count: 82, mengubah_data: 0 },
        { id: 17, name: "Yohanes", visited_count: 3, mengubah_data: 0 },
      ],
      colors: [
        "#4F6EF7",
        "#E85D75",
        "#2ABFA3",
        "#F59E0B",
        "#8B5CF6",
        "#10B981",
        "#F97316",
        "#06B6D4",
      ],
    };
  },
  computed: {
    rankedUsers() {
      return [...this.userData].sort(
        (a, b) =>
          b.visited_count +
          b.mengubah_data -
          (a.visited_count + a.mengubah_data),
      );
    },
    activeUsers() {
      return this.userData.filter((u) => !this.isInactive(u)).length;
    },
    inactiveUsers() {
      return this.userData.filter((u) => this.isInactive(u)).length;
    },
    maxVisits() {
      return Math.max(...this.userData.map((u) => u.visited_count), 1);
    },
    maxEdits() {
      return Math.max(...this.userData.map((u) => u.mengubah_data), 1);
    },
  },
  methods: {
    isInactive(user) {
      return user.visited_count === 0 && user.mengubah_data === 0;
    },
    avatarBg(name) {
      return this.colors[name.charCodeAt(0) % this.colors.length];
    },
    barWidth(value, type = "visits") {
      const max = type === "visits" ? this.maxVisits : this.maxEdits;
      return Math.round((value / max) * 100);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

* {
  box-sizing: border-box;
}

.leaderboard-wrap {
  font-family: "Inter", sans-serif;
  background: #f5f6fa;
  min-height: 100vh;
  padding: 2rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.leaderboard-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8eaf0;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  padding: 1.75rem 2rem;
  width: 100%;
  max-width: 780px;
}

/* Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 1.25rem;
}

.card-subtitle {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: #9ca3af;
  margin: 0 0 0.2rem;
}

.card-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.summary-pills {
  display: flex;
  gap: 0.5rem;
}

.pill {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.3rem 0.75rem;
  border-radius: 100px;
}

.pill-blue {
  background: #eef2ff;
  color: #4f6ef7;
}
.pill-gray {
  background: #f3f4f6;
  color: #6b7280;
}

.pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.pill-dot.blue {
  background: #4f6ef7;
}
.pill-dot.gray {
  background: #9ca3af;
}

/* Divider */
.divider {
  border: none;
  border-top: 1px solid #f0f1f5;
  margin-bottom: 1rem;
}

/* Column Labels */
.col-labels {
  display: grid;
  grid-template-columns: 2.5rem 1fr 140px 140px 80px;
  padding: 0 0.75rem;
  margin-bottom: 0.4rem;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #9ca3af;
}

/* List */
.leaderboard-list {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

/* Row */
.leaderboard-row {
  display: grid;
  grid-template-columns: 2.5rem 1fr 140px 140px 80px;
  align-items: center;
  padding: 0.75rem;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: background 0.15s, border-color 0.15s;
}

.leaderboard-row:hover {
  background: #f9fafb;
  border-color: #e8eaf0;
}

.leaderboard-row.row-top {
  background: #f5f7ff;
  border-color: #c7d2fe;
}

.leaderboard-row.row-inactive {
  opacity: 0.5;
}

/* Rank */
.rank {
  display: flex;
  justify-content: center;
}

.rank-medal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.7rem;
  font-weight: 700;
  color: #fff;
}
.gold {
  background: #f59e0b;
}
.silver {
  background: #9ca3af;
}
.bronze {
  background: #cd7f55;
}

.rank-number {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
}

/* User Cell */
.user-cell {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.user-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}
.user-name.name-muted {
  color: #6b7280;
}

/* Stat Cell */
.stat-cell {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.stat-value {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
}
.stat-value.muted {
  color: #9ca3af;
}

.mini-bar-wrap {
  height: 4px;
  background: #f0f1f5;
  border-radius: 2px;
  overflow: hidden;
  width: 100px;
}

.mini-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s ease;
}

.visits-bar {
  background: #4f6ef7;
}
.edits-bar {
  background: #e85d75;
}

/* Status */
.status-cell {
  display: flex;
  justify-content: center;
}

.badge {
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.7rem;
  border-radius: 100px;
}

.badge-active {
  background: #ecfdf5;
  color: #059669;
}

.badge-idle {
  background: #f3f4f6;
  color: #9ca3af;
}

@media (max-width: 600px) {
  .leaderboard-wrap {
    padding: 1rem;
  }
  .leaderboard-card {
    padding: 1.25rem;
  }
  .col-labels,
  .leaderboard-row {
    grid-template-columns: 2rem 1fr 90px 70px;
  }
  .status-cell {
    display: none;
  }
}
</style>
